import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormGroup } from '@angular/forms';
import { SubSink } from 'subsink';
import {
  tap,
  first,
  pairwise,
  map,
  distinct,
  finalize,
  concatMap,
} from 'rxjs/operators';
import { GroupedOption } from '@app/run-planning/model/option';
import { ComboBoxComponent } from '@app/cloud-run-prep/directives/combobox/combobox.component';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { IDynamicLoadingComponent } from '@app/run-planning/interface';
import { NUM_DROPDOWN_OPTIONS_LIMIT } from '@app/run-planning/constants';
import { HelperService } from '@app/run-planning/services/helper.service';
import { ReferenceFilesService } from '@stratus/gss-ng-sdk';
import { IReferenceFileFieldSettings, convertSystemTagFiltersToApiFormat } from '@app/run-planning/model/form-field';
import { GssApiService } from '@app/run-planning/services/gss-api-service';
import { LIST_GENOMES_PAGE_SIZE } from '@app/cloud-run-prep/constants';
import { ReferenceFileUploadOrchestratorService } from '@app/shared/services/reference-file-upload-orchestrator.service';
import { CodeFeaturesService } from '@app/core/services/user/code-features.service';

@Component({
  selector: 'app-reference-file-dropdown',
  templateUrl: './reference-file-dropdown.component.html',
  styleUrls: ['./reference-file-dropdown.component.scss']
})
export class ReferenceFileDropdownComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, IDynamicLoadingComponent {
  /* Reference File form control Id in the formGroup */
  @Input() referenceFileFormControlName: string;
  @Input() referenceFileFieldSettings: IReferenceFileFieldSettings = {};
  /* Analysis form field settings field used as filter parameter to fetch the options */
  @Input() referenceFileGenomeFilterFieldId: string;
  @Input() genomeFilterGenomeId: string;
  /* Analysis form field settings field used as filter parameter to fetch the options */
  @Input() referenceFileTypeFilter: string;
  @Input() systemTagFilters: Array<{ [key: string]: string }>;
  /* Hash table version of genomes allowed for the selected application, defined in AVD return from render call */
  @Input() genomeHashTableVersion: string;
  @Input() analysisVersionDefinitionId: string;
  /* Analysis form field required field, if optional, options will include clear field */
  @Input() isRequired = false;
  @Input() fieldLabel = 'Reference File';
  @Input() disableReferenceFileUpload: boolean;
  referenceFileOptions: GroupedOption[] = [];

  @Input() newReferenceFileName$: Observable<string>;
  @Output() newReferenceFileAdded = new EventEmitter<{
    formControlName: string,
    fileName: string,
    referenceFileId: string,
    isMultiValue: boolean,
  }>();

  private loadingSubject = new BehaviorSubject<boolean>(true);
  public isLoading$ = this.loadingSubject.asObservable();

  @ViewChild('referenceFileSelectCombobox', { static: true })
  referenceFileSelectCombobox: ComboBoxComponent;

  private depedentGenomeIdSubject = new BehaviorSubject<string>('');
  private dependentGenomeId$: Observable<string> = this.depedentGenomeIdSubject.asObservable();

  private systemTagFiltersSubject = new BehaviorSubject<Array<{ [key: string]: string }>>(null);
  private systemTagFilters$: Observable<Array<{ [key: string]: string }>> = this.systemTagFiltersSubject.asObservable();

  get parentFormGroup(): FormGroup { return this.controlContainer.control as FormGroup; }
  get refFileFormControl() { return this.parentFormGroup.get(this.referenceFileFormControlName); }

  private subs = new SubSink();
  private cachedValue;
  genomeDisplayName: string;
  effectiveSystemTagFilters: Array<{ [key: string]: string }>;
  private systemTagsFeatureEnabled: boolean;

  constructor(
    private controlContainer: ControlContainer,
    private helper: HelperService,
    private referenceFilesService: ReferenceFilesService,
    private gssApiService: GssApiService,
    private uploadOrchestrator: ReferenceFileUploadOrchestratorService,
    private codeFeaturesService: CodeFeaturesService,
  ) {
    this.systemTagsFeatureEnabled = this.codeFeaturesService.runPlanningSystemTagsEnabled;
  }

  ngOnInit() {
    // Initialize dropdown options
    this.referenceFileOptions = this.getEmptyOptions();
    this.subscribeToOptionsDependencies();

    this.subs.sink = this.isLoading$.pipe(pairwise()).subscribe(([preIsLoading, currentIsLoading]) => {
      if (preIsLoading != currentIsLoading) {
        // Only cache/refetch the value when loading status changed
        if (currentIsLoading) {
          // To cache the value while it begins to load
          this.cachedValue = this.refFileFormControl.value;
          this.clearSelection();
        } else if (this.cachedValue) {
          // If have any cached value, patch back and clear the cache
          let cachedValue = this.cachedValue;
          if (this.referenceFileFieldSettings.isMultiSelect) {
            this.refFileFormControl.patchValue([...cachedValue, ...(this.refFileFormControl.value || [])]);
          } else {
            this.refFileFormControl.patchValue(this.cachedValue);
          }
          this.cachedValue = undefined;
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('genomeFilterGenomeId')) {
      this.depedentGenomeIdSubject.next(changes.genomeFilterGenomeId.currentValue);
      this.resolveGenomeDisplayName(changes.genomeFilterGenomeId.currentValue);
    }
    if (changes.hasOwnProperty('systemTagFilters')) {
      const filters = this.systemTagsFeatureEnabled ? changes.systemTagFilters.currentValue : null;
      this.effectiveSystemTagFilters = filters;
      this.systemTagFiltersSubject.next(filters);
    }
  }

  /**
   * Resolve genome display name from genome id by calling listGenomes API (same as what genome dropdown component does).
   * Response will be cached by GssApiCacheService so it is fine to call on every render.
   * Have to call API because the state only stores genome id, not display name.
   * @param genomeId
   */
  private resolveGenomeDisplayName(genomeId: string) {
    if (!genomeId) {
      this.genomeDisplayName = null;
      return;
    }
    var params: any = { pageSize: LIST_GENOMES_PAGE_SIZE };
    if (this.analysisVersionDefinitionId) {
      params.analysisVersionDefinitionId = this.analysisVersionDefinitionId;
    }
    this.subs.sink = this.gssApiService.listGenomes(params).pipe(first()).subscribe({
      next: function(response) {
        var match = (response.items || []).find(function(g) { return g.id === genomeId; });
        this.genomeDisplayName = match ? (match.displayName || match.name || genomeId) : genomeId;
      }.bind(this),
      error: function() { this.genomeDisplayName = genomeId; }.bind(this)
    });
  }

  /**
   * Fetch the options list whenever the specified genomeFilterField value changes
   * and/or parent dynamic-settings-form component request for re-update due to new reference file addded
   *
   * Notes on the 3 observables to combine:
   *
   * `this.dependentGenomeId$`: When a different genome value is selected on the dependent field
   * `this.newReferenceFileName$`: When a new reference file is added via popup modal,
   *    emits the filename of the added file to all reference file dropdowns.
   * `this.systemTagFilters$`: When systemTagFilters change (e.g. after a render AVD response).
   * `this.referenceFileGenomeFilterFieldId` is null if this field has no dependency on a genome.
   *    --> If null, then just getOptions$ without genome id
   *    --> If not null and genomeId is set, getOptions$ with genome id
   *    --> If not null but genomeId is empty, and systemTagFilters present, fetch with filters only
   *    --> If not null but genomeId is empty, and no systemTagFilters, return empty options
   */
  private subscribeToOptionsDependencies() {
    this.subs.sink = combineLatest(this.dependentGenomeId$, this.newReferenceFileName$, this.systemTagFilters$)
      .pipe(
        distinct(),
        tap(() => this.loadingSubject.next(true)),
        concatMap(([genomeId, _, systemTagFilters]) => {
          if (!this.referenceFileGenomeFilterFieldId) {
            return this.getOptions$(null, this.referenceFileTypeFilter, systemTagFilters);
          } else if (genomeId) {
            return this.getOptions$(genomeId, this.referenceFileTypeFilter, systemTagFilters);
          } else if (systemTagFilters && systemTagFilters.length > 0) {
            // Genome not selected yet, but systemTagFilters can still produce a filtered fetch
            return this.getOptions$(null, this.referenceFileTypeFilter, systemTagFilters);
          } else {
            return of(this.getEmptyOptions());
          }
        }),
        finalize(() => this.loadingSubject.next(false)),
      )
      .subscribe({
        next: groupedOptions => {
          this.referenceFileOptions = groupedOptions;
          this.loadingSubject.next(false);
        },
        error: error => console.error(error),
      });
  }

  registerOnChange(fn: any): void {
  }

  registerOnTouched(fn: any): void {
  }

  writeValue(obj: any): void {
  }

  clearSelection() {
    this.controlContainer.control.get(this.referenceFileFormControlName).patchValue(null);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private getOptions$(genomeId: string, fileType: string, systemTagFilters?: Array<{ [key: string]: string }>) {
    const requestParams: ReferenceFilesService.ListReferenceFilesParams = {
      genomeId,
      type: fileType,
      sort: 'displayName',
      pageSize: NUM_DROPDOWN_OPTIONS_LIMIT
    };
    const systemTags = convertSystemTagFiltersToApiFormat(systemTagFilters);
    if (systemTags.length > 0) {
      requestParams.systemTags = systemTags;
    }
    return this.referenceFilesService.listReferenceFiles(requestParams)
      .pipe(
        map(pagedItems => pagedItems.items.map(x => this.helper.mapToCustomOption(x))),
        first(),
        map(allCustomOptions => this.helper.groupByCustomAndStandardOptions(allCustomOptions, 'Reference Files'))
      );
  }

  private getEmptyOptions() {
    return this.helper.groupByCustomAndStandardOptions([], 'Reference Files');
  }

  /**
   * The page will be blocked by
   * - loading spinner during
   *    1. filename validation
   *    2. gettting existing file with the same name
   * - progress modal during file upload
   */
  async handleAddNewOption(event) {
    const importedFile = (event.target as HTMLInputElement).files[0];
    if (!importedFile) {
      event.target.value = '';
      return;
    }

    try {
      const result = await this.uploadOrchestrator.uploadReferenceFile(importedFile, {
        fileType: this.referenceFileTypeFilter,
        genomeFilterGenomeId: this.genomeFilterGenomeId,
        genomeHashTableVersion: this.genomeHashTableVersion,
        analysisVersionDefinitionId: this.analysisVersionDefinitionId,
      });

      if (result) {
        this.newReferenceFileAdded.emit({
          formControlName: this.referenceFileFormControlName,
          fileName: result.fileName,
          referenceFileId: result.referenceFileId,
          isMultiValue: this.referenceFileFieldSettings.isMultiSelect,
        });
      }
    } finally {
      event.target.value = '';
    }
  }
}
